import React, {useState} from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import "../../../components/common/bt.css";

const SalaryPaymentReportTable = ({ printMode = false }) => {
  const { list, loading, filters } = useSelector(
    (state) => state.salaryPaymentReport
  );

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };
  
  const withoutDetailsColumns = [
    { title: "No.", render: (_, __, index) => index + 1 },
    { title: "VCH No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (value) => (value ? dayjs(value).format("DD-MMM-YYYY") : "-"),
    },
    { title: "Payment From Account", dataIndex: "acc_name" },
    { title: "Month", dataIndex: "month" },
    { title: "Payment For", dataIndex: "pay_type" },
    { title: "Payment By", dataIndex: "user" },
    { title: "Total Amount", dataIndex: "total" },
  ];

  const withDetailsColumns = [
    { title: "No.", render: (_, __, index) => index + 1 },
    { title: "VCH No", dataIndex: "vch_no" },
    {
      title: "Date",
      dataIndex: "date",
      render: (value) => (value ? dayjs(value).format("DD-MMM-YYYY") : "-"),
    },
    { title: "Payment From Account", dataIndex: "acc_name" },
    { title: "Month", dataIndex: "month" },
    { title: "Payment By", dataIndex: "user" },
    { title: "Employee", dataIndex: "employee" },
    { title: "Payment For", dataIndex: "pay_type" },
    { title: "Amount", dataIndex: "subtotal" },
  ];

 


  const totals = list.reduce((sum, row) => {
  return sum + Number(row.subtotal || 0);
}, 0);

const grandTotal = list.reduce((sum, row) => sum + Number(row.subtotal || row.total || 0), 0);

   const expandable =
  filters.recordType === "with details" || filters.filterType === "By Employee" || filters.filterType === "By Type"
    ? {
        expandedRowRender: (record) => (
          <Table
            rowKey="id"
            columns={[
              { title: "Employee", dataIndex: "employee" },
              { title: "Payment For", dataIndex: "pay_type" },
              { title: "Amount", dataIndex: "amount" },
            ]}
            dataSource={record.details}
            pagination={false}
            size="small"
          />
        ),
        rowExpandable: (record) => Array.isArray(record.details) && record.details.length > 0,
      }
    : undefined;

  if (printMode) {
    const isWithDetails =
      filters.recordType === "with details" ||
      filters.filterType === "By Employee" ||
      filters.filterType === "By Type";

    return (
      <table className="ledger-print-table">
        <colgroup>
          {isWithDetails ? (
            <>
              <col style={{ width: "4%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "9%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "13%" }} />
            </>
          ) : (
            <>
              <col style={{ width: "4%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "19%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "16%" }} />
            </>
          )}
        </colgroup>
        <tbody>
          <tr className="ledger-print-heading">
            <td>No.</td>
            <td>VCH No</td>
            <td>Date</td>
            <td style={{ whiteSpace: "normal" }}>Payment From Account</td>
            <td>Month</td>
            {isWithDetails ? (
              <>
                <td>Payment By</td>
                <td>Employee</td>
                <td>Payment For</td>
                <td style={{ textAlign: "right" }}>Amount</td>
              </>
            ) : (
              <>
                <td>Payment For</td>
                <td>Payment By</td>
                <td style={{ textAlign: "right" }}>Total Amount</td>
              </>
            )}
          </tr>

          {list.map((row, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              <td>{row.vch_no}</td>
              <td>{row.date ? dayjs(row.date).format("DD-MMM-YYYY") : "-"}</td>
              <td style={{ whiteSpace: "normal" }}>{row.acc_name}</td>
              <td>{row.month}</td>
              {isWithDetails ? (
                <>
                  <td>{row.user}</td>
                  <td>{row.employee}</td>
                  <td>{row.pay_type}</td>
                  <td style={{ textAlign: "right" }}>
                    {Number(row.subtotal || 0).toFixed(2)}
                  </td>
                </>
              ) : (
                <>
                  <td>{row.pay_type}</td>
                  <td>{row.user}</td>
                  <td style={{ textAlign: "right" }}>
                    {Number(row.total || 0).toFixed(2)}
                  </td>
                </>
              )}
            </tr>
          ))}

          <tr className="ledger-total-row">
            <td
              colSpan={isWithDetails ? 8 : 7}
              style={{ textAlign: "right" }}
            >
              Grand Total
            </td>
            <td style={{ textAlign: "right" }}>
              {grandTotal.toFixed(2)}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <Table
      rowKey="id"
      columns={
        filters.recordType === "with details"
          ? withDetailsColumns
          : withoutDetailsColumns
      }
      dataSource={list}
      data-testid="salary-record-table"
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}
      scroll={printMode ? undefined : { x: "max-content" }}
      bordered
      expandable={filters.recordType === "with details" || filters.filterType === "By Employee " || filters.filterType === "By Type" ? expandable : undefined}
      size={printMode ? "small" : "middle"}
      summary={() => {
        if (filters.recordType === "with details") {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={9}>
                <strong>Grand Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <strong>{grandTotal?.toFixed(2)}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        } else {
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={6} align="right">
                <strong>Grand Total:</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <strong>{grandTotal?.toFixed(2)}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }
      }}
    />
  );
};

export default SalaryPaymentReportTable;
